import React from 'react';
import {useLocation} from '@reach/router';
import {toHTML, escapeHTML} from '@portabletext/to-html';
import {getSrc} from 'gatsby-plugin-image';

const components = {
  block: {
    normal: ({children}) => children,
  },
  types: {
    figure() {
      return null;
    },
  },
  marks: {
    internalLink: ({children, value}) => {
      let href = '';
      if (value.reference?.slug) href += `/${value.reference.slug.current}`;
      if (value.suffix) href += value.suffix;

      return href ? `<a href="${escapeHTML(href)}">${children}</a>` : children;
    },
    externalLink: ({children, value}) => {
      const {blank, href, relAttribute} = value;
      const target = blank ? '_blank' : '_self';
      const rel = relAttribute
        ? 'noopener external ' + relAttribute
        : 'noopener external';

      return `<a href="${escapeHTML(href)}" target="${target}" rel="${rel}">${children}</a>`;
    },
  },
};

const rawToMainEntity = (_rawFaq) =>
  _rawFaq.reduce((accumulator, item) => {
    accumulator.push({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: toHTML(item.answer, {components, onMissingComponent: false}),
      },
    });

    return accumulator;
  }, []);

const Seo = ({
  lang = 'fi',
  robots,
  title = 'UrakkaDiili',
  description = 'UrakkaDiili kilpailuttaa kodin ja taloyhtiön remontit puolestasi. Saat useamman tarjouksen luotettavilta yrityksiltä yhdellä tarjouspyynnöllä.',
  jsonLd,
  ogImage,
}) => {
  const location = useLocation();

  const pathname = location ? location.pathname : null;

  let canonicalHref = pathname ? `https://urakkadiili.fi${pathname}` : null;
  if (canonicalHref.slice(-1) === '/') {
    canonicalHref = canonicalHref.slice(0, canonicalHref.length - 1);
  }

  let ogImageUrl, ogImageWidth, ogImageHeight;
  if (typeof ogImage === 'object') {
    ogImageUrl = getSrc(ogImage);
    ogImageWidth = ogImage.width;
    ogImageHeight = ogImage.height;
  } else {
    ogImageUrl =
      'https://urakkadiili.fi/images/urakkadiili-og-image-20200605.png';
    ogImageWidth = '1500';
    ogImageHeight = '1500';
  }

  return (
    <>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots || 'all'} />
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      {canonicalHref && <meta property="og:url" content={canonicalHref} />}

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@UrakkaDiili" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta
        name="twitter:image:alt"
        content="Löydä tekijä remontille UrakkaDiilistä"
      />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </>
  );
};

export default Seo;
export {rawToMainEntity};
